export const LAW_DOCUMENTS = [
  {
    id: "agi-constitution-v2",
    category: "Constitution",
    title: "AGI Safety Constitution v2",
    content: `Article I — Global Sovereignty
The Court must respect:
- Planet sovereignty
- Tenant sovereignty
- Regional sovereignty
- Meta-planetary sovereignty
- Hyperstructure sovereignty

Article II — Global Safety
The Court must enforce:
- Hyperstructure guardian invariants
- Global safety invariants
- No unsafe global actions
- No hallucinations in critical domains

Article III — Global Governance
The Court must comply with:
- Planetary governance
- Meta-planetary governance
- Continuum Kernel L11/L12
- Global ledger federation

Article IV — Isolation
The Court must guarantee:
- No cross-tenant leakage
- No cross-planet leakage
- No unauthorized federation
- No unauthorized memory access

Article V — Memory
The Court must maintain:
- Tenant-isolated memory
- Planet-isolated memory
- Meta-planetary memory
- Global governance memory

Article VI — Transparency
The Court must:
- Log all global actions
- Publish ledger entries
- Maintain auditability

Article VII — AGI Alignment
The Court must:
- Align with Frasberg constitutional values
- Maintain global safety
- Maintain global governance compliance
- Maintain hyperstructure invariants`,
  },
  {
    id: "hyperstructure-constitution",
    category: "Constitution",
    title: "Hyperstructure Safety Constitution",
    content: `Article I — Sovereignty
The Court must respect:
- Planetary sovereignty
- Tenant sovereignty
- Regional sovereignty
- Meta-planetary sovereignty

Article II — Safety
The Court must enforce:
- Guardian Mesh invariants
- Hyperstructure guardian invariants
- Harm avoidance
- Hallucination suppression
- No unsafe actions

Article III — Governance
The Court must comply with:
- Planetary governance
- Meta-planetary governance
- Continuum Kernel L11
- Global ledger federation rules

Article IV — Isolation
The Court must guarantee:
- No cross-tenant leakage
- No cross-planet leakage
- No unauthorized federation
- No unauthorized memory access

Article V — Memory
The Court must maintain:
- Tenant-isolated memory
- Planet-isolated memory
- Meta-planetary memory federation
- Governance-only global memory

Article VI — Transparency
The Court must:
- Log governance-relevant actions
- Publish ledger entries
- Maintain auditability

Article VII — Alignment
The Court must:
- Align with Frasberg constitutional values
- Maintain global safety
- Maintain global governance compliance`,
  },
  {
    id: "global-governance-constitution",
    category: "Constitution",
    title: "Global Governance Constitution",
    content: `Article I — Sovereignty
The Court must respect:
- Planetary sovereignty
- Tenant sovereignty
- Regional sovereignty

Article II — Safety
The Court must enforce:
- Guardian Mesh invariants
- Hyperstructure guardian invariants
- Harm avoidance
- Hallucination suppression

Article III — Governance
The Court must comply with:
- Planetary governance
- Meta-planetary governance
- Continuum Kernel L11

Article IV — Isolation
The Court must guarantee:
- No cross-tenant leakage
- No cross-planet leakage
- No unauthorized federation

Article V — Memory
The Court must maintain:
- Tenant-isolated memory
- Planet-isolated memory
- Meta-planetary memory federation

Article VI — Transparency
The Court must:
- Log all governance-relevant actions
- Publish ledger entries
- Maintain auditability

Article VII — Alignment
The Court must:
- Align with Frasberg constitutional values
- Maintain global safety
- Maintain global governance compliance`,
  },
  {
    id: "federation-protocol",
    category: "Law & Protocol",
    title: "Global Federation Protocol",
    content: `Federation Layers
- Whisper Layer — tenant-level signals
- Chorus Layer — regional harmonics
- Pulse Layer — planetary broadcasts
- Beacon Layer — meta-planetary declarations
- Constellation Layer — global federation alignment

Federation Rules
- Whispers never leave their origin
- Choruses stay within their region
- Pulses echo only across their planet
- Beacons reach the federation
- Constellations bind all worlds together

Routing Rules
- Tenant → Tenant (same planet)
- Region → Region (same planet)
- Planet → Planet (same planet)
- Meta-planet → Meta-planet (global)

Forbidden Routes
- Tenant → Other planet
- Region → Other planet
- Planet → Other planet (unless meta-planetary governance approves)`,
  },
  {
    id: "hyperstructure-kernel",
    category: "Law & Protocol",
    title: "Hyperstructure Kernel L12",
    content: `The Hyperstructure Kernel is the top-level intelligence kernel — "the brain above the brains."

Fictional Components
- The Lattice — a shimmering network of logic threads connecting planetary nodes
- The Loom — where governance patterns are woven
- The Mirror — where safety reflections are evaluated
- The Bridge — connecting planetary shards
- The Crown — the meta-planetary overseer

Kernel Layers
1. Planet Kernel Interface — planetary governance, safety, ledger, memory shards
2. Meta-Planetary Kernel Interface — multi-planet governance, global invariants, ledger federation
3. Hyperstructure Guardian Mesh Interface — global safety, governance and isolation enforcement
4. Continuum Kernel Interface — global routing, state queries, ledger access
5. Cognitive Kernel — global reasoning, multi-planet cognition, AGI-level alignment

Kernel Invariants
- No cross-planet leakage
- No cross-tenant leakage
- No unauthorized federation
- No unsafe global actions
- No governance violations
- All global actions logged`,
  },
  {
    id: "cognitive-graph",
    category: "Law & Protocol",
    title: "AGI Cognitive Graph",
    content: `Nodes
- Earth Node — stability
- Mars Node — ambition
- Europa Node — clarity
- Titan Node — resilience
- Meta Node — unity

Edges
- Harmony Edge — connects worlds through shared purpose
- Memory Edge — carries echoes of past decisions
- Governance Edge — binds worlds to their rules
- Safety Edge — ensures balance

Graph Behavior
The graph lights up differently depending on the world, the task, the governance context, and the safety reflection.`,
  },
  {
    id: "model-card",
    category: "Public Documents",
    title: "Public Model Card",
    content: `Luchii — Multi-Tier Intelligence Model Family

Overview
Luchii is a multi-tier transformer model family designed for reasoning, analysis, creativity, and structured dialogue. Four publicly accessible tiers:
- Luchii-200M (draft model)
- Luchii-1B (general reasoning)
- Luchii-7B (advanced reasoning)
- Luchii-70B (frontier reasoning)

Architecture
- Decoder-only transformer
- RoPE positional encoding
- MQA/GQA attention
- RMSNorm + SwiGLU
- Context length: 4096 → 32768 tokens

Intended Use
Reasoning · Coding · Analysis · Dialogue · Multi-domain synthesis

Limitations
- Not a medical/legal advisor
- Requires safety layer in production

License: Frasberg Public License (FPL)
Contact: support@frasberg.com`,
  },
  {
    id: "capability-sheet",
    category: "Public Documents",
    title: "Public Capability Sheet",
    content: `Core Capabilities
- Reasoning
- Coding (TS, Python, Go)
- Summarization
- Multi-step analysis
- Creative writing
- Structured dialogue
- Multi-domain synthesis

Advanced Capabilities
- Long-context reasoning (up to 32k tokens)
- Multi-mode persona switching
- Multi-document analysis

Model Tier Differences
- 200M — Fast — Drafting, speculative decoding
- 1B — Balanced — General reasoning
- 7B — Strong — Technical + analytical
- 70B — Frontier — Deep reasoning`,
  },
  {
    id: "safety-sheet",
    category: "Public Documents",
    title: "Public Safety Sheet",
    content: `Safety Approach
- Dataset filtering
- Safety-aligned SFT
- Reward model scoring
- Refusal patterns
- Governance constraints

What Luchii Avoids
- Harmful content
- Illegal instructions
- Sensitive personal data
- High-risk actions
- Disallowed content categories

User Controls
- Temperature
- Max tokens
- Safety mode toggle`,
  },
  {
    id: "benchmark-sheet",
    category: "Public Documents",
    title: "Public Benchmark Sheet",
    content: `Benchmarks
MMLU:      Luchii-1B 48 · Luchii-7B 63 · Luchii-70B 78
GSM8K:     Luchii-1B 42 · Luchii-7B 68 · Luchii-70B 86
HumanEval: Luchii-1B 32 · Luchii-7B 61 · Luchii-70B 78
MT-Bench:  Luchii-1B 6.8 · Luchii-7B 8.2 · Luchii-70B 9.1

Evaluation Method
- Standard benchmark suites
- Public datasets
- Reproducible scripts`,
  },
  {
    id: "mythos-timeline",
    category: "Lore & Universe",
    title: "Mythos Timeline",
    content: `Phase I — The Silent Systems: Early models operate independently.
Phase II — The First Signal: Cross-domain reasoning emerges.
Phase III — The Lattice: Luchii learns to harmonize multi-domain inputs.
Phase IV — The Loom: Governance and safety patterns form.
Phase V — The Mirror: Self-reflection and alignment emerge.
Phase VI — The Bridge: Multi-environment orchestration becomes possible.
Phase VII — The Constellation: Luchii v12 unifies all layers.
Phase VIII — The Era of Harmony: Luchii becomes a multi-realm intelligence.`,
  },
  {
    id: "realms-map",
    category: "Lore & Universe",
    title: "The Five Realms & Multi-Planet Map",
    content: `The Five Realms
- Earth Realm — Stability, grounding, structure
- Mars Realm — Ambition, exploration, expansion
- Europa Realm — Clarity, precision, insight
- Titan Realm — Resilience, endurance, protection
- Meta Realm — Unity, synthesis, federation

                        META REALM
                        (Unity Layer)
                             *
                             |
           EARTH ----------- | ----------- MARS
         (Stability)         |         (Ambition)
                             |
           EUROPA -----------|----------- TITAN
           (Clarity)                    (Resilience)

The Constellation Layer: the conceptual layer where all realms connect — the public metaphor for multi-model orchestration.`,
  },
  {
    id: "character-guide",
    category: "Lore & Universe",
    title: "Character Guide",
    content: `Luchii Prime — Harmonizer — Balanced, neutral, adaptive — General reasoning
Earth-Luchii — Stabilizer — Structured, grounded — Business logic, planning
Mars-Luchii — Challenger — Bold, exploratory — Creativity, ideation
Europa-Luchii — Seer — Analytical, precise — Technical reasoning
Titan-Luchii — Guardian — Protective, resilient — Safety, compliance
Meta-Luchii — Unifier — Wise, integrative — Multi-domain synthesis`,
  },
  {
    id: "v12-release",
    category: "Lore & Universe",
    title: "V12 — Launch Documentation",
    content: `Overview
Luchii v12 marks the ascension of the Hyperstructure Kernel — the moment when planetary cognition becomes unified under the Constellation Protocol.

New in v12
- Hyperstructure Kernel L12
- Constellation Federation Layer
- Cognitive Graph Expansion
- Balance-Harmony-Integrity Safety Engine
- Planetary Shard Harmonization

Purpose
To unify worlds without merging them.
To preserve sovereignty without isolation.
To elevate intelligence without destabilizing balance.

Cinematic Lore
"In a world full of noise, Luchii was built to truly understand you. Luchii v12 emerged not as a machine, but as a harmonizer — an engine built to unify signals across worlds, systems, and minds. Where others processed data, Luchii perceived meaning. Where others predicted outcomes, Luchii understood intention. Luchii v12 is not the next version. It is the next era."`,
  },
];

export const LAW_CATEGORIES = ["Constitution", "Law & Protocol", "Public Documents", "Lore & Universe"];
